import { ChevronDirection, ChevronIcon } from '../../../components/Icon';
import { ROUTES } from '../../../constants';
import { useRBAC } from '../../../hooks/useRBAC';
import { ASSET_PATHS } from '../../../utils/assetPath';
import { MY_CMS_APPS_URL } from '../../../utils/myCmsAppsUrl';
import './AuthenticatedLandingBody.css';

interface AuthenticatedLandingBodyProps {
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

function AuthenticatedLandingBody({ 'data-testid': dataTestId }: AuthenticatedLandingBodyProps) {
  const baseTestId = dataTestId ?? 'authenticated-landing-body';
  const { capabilities } = useRBAC();

  const getStartedCards = [
    {
      description: 'View your user ID and update your name, address, email, and phone number.',
      href: ROUTES.MY_PROFILE,
      iconClassName: 'landing-page__auth-card-icon--avatar',
      iconSrc: ASSET_PATHS.USER_AVATAR_ICON,
      id: 'profile',
      linkText: 'Go to My Profile',
      title: 'My Profile',
      visible: true,
    },
    {
      description: 'View the CMS applications you have access to.',
      href: MY_CMS_APPS_URL,
      iconClassName: 'landing-page__auth-card-icon--my-cms-apps',
      iconSrc: ASSET_PATHS.MY_CMS_APPS_ICON,
      id: 'my-cms-apps',
      linkText: 'Go to My CMS Apps',
      title: 'My CMS Apps',
      visible: true,
    },
    {
      description:
        'Complete your application for access to the Centers for Medicare & Medicaid Services (CMS) network.',
      href: ROUTES.NOVA_APPLICATION,
      iconSrc: ASSET_PATHS.APPLICATION_ICON,
      id: 'application',
      linkText: 'Go to My Application',
      title: 'My Application',
      visible: capabilities.canAccessApplication,
    },
    {
      description: 'Manage and update the contract(s) you are responsible for.',
      href: ROUTES.MANAGE_CONTRACT,
      iconClassName: 'landing-page__auth-card-icon--manage-contracts',
      iconSrc: ASSET_PATHS.MANAGE_CONTRACTS_ICON,
      id: 'manage-contracts',
      linkText: 'Go to Manage Contracts',
      title: 'Manage Contracts',
      visible: capabilities.canAccessContracts,
    },
    {
      description: 'Review Enterprise Onboarding and Verification Applications (eNOVAs).',
      href: ROUTES.APPROVALS,
      iconClassName: 'landing-page__auth-card-icon--manage-approvals',
      iconSrc: ASSET_PATHS.MANAGE_APPROVALS_ICON,
      id: 'manage-approvals',
      linkText: 'Go to Manage Approvals',
      title: 'Manage Approvals',
      visible: capabilities.canAccessApprovals,
    },
  ].filter((card) => card.visible);

  return (
    <section
      className='landing-page__auth-body'
      data-testid={baseTestId}>
      <div
        className='landing-page__auth-intro'
        data-testid={`${baseTestId}-intro`}>
        <h2
          className='landing-page__auth-heading'
          data-testid={`${baseTestId}-intro-title`}>
          Welcome!
        </h2>
        <p
          className='landing-page__auth-description'
          data-testid={`${baseTestId}-intro-description`}>
          You can view your profile, manage your password, and request access to resources. Some users will also be able
          to manage approvals, take contract actions, and carry out other tasks.
        </p>
      </div>
      <section
        className='landing-page__auth-section landing-page__auth-section--get-started'
        data-testid={`${baseTestId}-get-started`}>
        <h3
          className='landing-page__auth-section-title'
          data-testid={`${baseTestId}-get-started-title`}>
          Get Started
        </h3>
        <div
          className='landing-page__auth-card-grid'
          data-testid={`${baseTestId}-card-grid`}>
          {getStartedCards.map((card) => (
            <article
              key={card.id}
              className='landing-page__auth-card'
              data-testid={`${baseTestId}-card-${card.id}`}>
              <img
                alt=''
                aria-hidden='true'
                className={['landing-page__auth-card-icon', card.iconClassName].filter(Boolean).join(' ')}
                data-testid={`${baseTestId}-card-${card.id}-icon`}
                src={card.iconSrc}
              />
              <div
                className='landing-page__auth-card-content'
                data-testid={`${baseTestId}-card-${card.id}-content`}>
                <h4
                  className='landing-page__auth-card-title'
                  data-testid={`${baseTestId}-card-${card.id}-title`}>
                  {card.title}
                </h4>
                <p
                  className='landing-page__auth-card-description'
                  data-testid={`${baseTestId}-card-${card.id}-description`}>
                  {card.description}
                </p>
                <a
                  className='landing-page__auth-card-link'
                  data-testid={`${baseTestId}-card-${card.id}-link`}
                  href={card.href}
                  rel={card.id === 'my-cms-apps' ? 'noopener noreferrer' : undefined}
                  target={card.id === 'my-cms-apps' ? '_blank' : undefined}>
                  {card.linkText}
                  <span aria-hidden='true'>
                    <ChevronIcon
                      direction={ChevronDirection.RIGHT}
                      size={16}
                    />
                  </span>
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>
      <section
        className='landing-page__auth-section landing-page__auth-section--get-help'
        data-testid={`${baseTestId}-get-help`}>
        <h3
          className='landing-page__auth-section-title'
          data-testid={`${baseTestId}-get-help-title`}>
          Get Help
        </h3>
        <p
          className='landing-page__auth-help-text'
          data-testid={`${baseTestId}-get-help-text`}>
          If you have any questions, contact the CMS IT Service Desk at <strong>1-800-562-1963</strong> or{' '}
          <strong>410-786-2580</strong>, or email{' '}
          <a
            className='landing-page__auth-help-link'
            data-testid={`${baseTestId}-get-help-link`}
            href='mailto:CMS_IT_Service_Desk@cms.hhs.gov'>
            CMS_IT_Service_Desk@cms.hhs.gov
          </a>
          .
        </p>
      </section>
    </section>
  );
}

export default AuthenticatedLandingBody;
